import React, { useEffect, useRef, useState } from 'react';
import { Text, Button, makeStyles } from '@fluentui/react-components';
import { db } from '@/firebase/config';
import { collection, query, orderBy, addDoc, onSnapshot,getDocs  } from 'firebase/firestore';
import { useUserPrefs } from '@/context/UserPrefsContext';
import { cityRoutineCategories } from '@/types'; // Make sure CityRoutineKey is exported as an enum or object, not just a type

export interface NewsComment {
  id: string;
  parentId: string | null;  
  newsId: string | null;
  user: string;
  timestamp: number;
  title: string;
  description: string;
  imageUrl: string;
  replies?: NewsComment[];
}

const useStyles = makeStyles({
  root: { display: 'flex', flexDirection: 'column', gap: '6px', fontSize: '12px' },
  row: {
  display: 'flex',
  flexDirection: 'row', // horizontal
  alignItems: 'center',
  gap: '6px',
  flexWrap: 'wrap', // allow wrapping on small screens
},
  author: { fontWeight: 600 },
  meta: { color: '#888', fontSize: '11px' },
  content: { fontSize: '12px' },
  replyBtn: { fontSize: '11px', padding: '0 6px', height: '18px' },
  inputRow: { display: 'flex', gap: '4px', marginTop: '2px', flexWrap: 'wrap' },
  input: {  flex: '1 1 auto',   // keeps them inline and resizable
  maxWidth: '80px',  // <-- limit width
   fontSize: '12px', padding: '2px 4px', height: '20px', border: '1px solid #ccc', borderRadius: '2px' },
  children: { marginLeft: '14px', borderLeft: '1px solid #eee', paddingLeft: '6px', marginTop: '4px', display: 'flex', flexDirection: 'column', gap: '6px' },
  loginHint: { color: '#c00', fontSize: '11px' },
});

interface CommentTreeProps {
  newsId?: string;     // city news article id
  routineKey?: string;
  dateKey?: string;   // 👈 ADD THIS
  onLoginClick?: () => void;
}


export const CommentTree: React.FC<CommentTreeProps> = ({
  newsId,
  routineKey,
  dateKey,
  onLoginClick,
}) => {
// console.log('comment query params', {
//   newsId,
//   routineKey,
//   dateKey,
// });

  const styles = useStyles();
  const { prefs } = useUserPrefs();

  const [comments, setComments] = useState<NewsComment[]>([]);
  const [activeReplyId, setActiveReplyId] = useState<string | null>(null);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [imageUrl, setImageUrl] = useState('');

  const isRoutine = Boolean(routineKey);
  const isLoggedIn = Boolean(prefs.userId);

    const isEvents = location.pathname.endsWith('/events');
const category = isEvents ? 'events' : 'news';
  // Get Firestore collection reference
  const getCommentsCollection = (parentId: string | null) => {
    if (!prefs.country || !prefs.state || !prefs.city || !prefs.language) {
      return null;
    }
//console.log('getCommentsCollection','newsId:', newsId,'routineKey:', routineKey,'dateKey:', dateKey,'parentId:', parentId,' newsid:', newsId);
    // 🟢 CITY NEWS (date + newsId)
    if ((routineKey === prefs.city ||  routineKey === 'events') && dateKey && newsId) {
      return collection(
        db,
        `CityRoutinePosts/${prefs.country}/${prefs.state}/${prefs.city}/${prefs.language}/${category}/${dateKey}/${newsId}/comments`
      );
    }

    // 🔵 ROUTINE POSTS
    if (routineKey && routineKey !== prefs.city  && routineKey !== 'events' &&  cityRoutineCategories.some(c => c.key === routineKey)) {
      if (!parentId) {
        // root posts
        return collection(
          db,
          `CityRoutinePosts/${prefs.country}/${prefs.state}/${prefs.city}/${prefs.language}/${routineKey}/posts`
        );
      } else {
        // child comments under a post
        return collection(
          db,
          `CityRoutinePosts/${prefs.country}/${prefs.state}/${prefs.city}/${prefs.language}/${routineKey}/posts/${parentId}/comments`
        );
      }
    }
    // 🟣 REGULAR NEWS
    if (
      newsId &&
      routineKey !== prefs.city &&    routineKey !== 'events' &&
      !cityRoutineCategories.some(c => c.key === routineKey)
    ) {
      return collection(db, 'newsComments', newsId, 'comments');
    }

    return null;
};


  // Load comments
 useEffect(() => {
  if (!newsId && !routineKey) {
    setComments([]);
    return;
  }

  const colRef = getCommentsCollection(null);
  // console.log('Subscribing to comments collection:', colRef?.path);
  if (!colRef) return;

  const q = query(colRef, orderBy('timestamp', 'asc'));
  const unsub = onSnapshot(q, async snap => {
    const roots: NewsComment[] = snap.docs.map(d => ({
      id: d.id,
      ...(d.data() as Omit<NewsComment, 'id'>),
      replies: [],
    }));

    if (isRoutine && routineKey &&    routineKey != 'events' && cityRoutineCategories.some(c => c.key === routineKey)) {
      // fetch children for each root post
      const rootsWithReplies = await Promise.all(
        roots.map(async root => {
          const childCol = getCommentsCollection(root.id);
          if (!childCol) return root;

          const childSnap = await getDocs(query(childCol, orderBy('timestamp', 'asc')));
          root.replies = childSnap.docs.map(doc => ({
            id: doc.id,
            ...(doc.data() as Omit<NewsComment, 'id'>),
            replies: [],
          }));
          //console.log('Fetched', root.replies.length, 'replies for post', root.id,' root.replies:',  root.replies);
          return root;
        })
      );
//console.log('rootsWithReplies',rootsWithReplies);

              setComments(rootsWithReplies);
    } else {
       if(routineKey == prefs.city ||  routineKey === 'events'){ 
         setComments(roots);
       }else {
         // city news, build tree from parentId
         const map = new Map<string, NewsComment>();

        roots.forEach(c => map.set(c.id, c));

        const tree: NewsComment[] = [];

        roots.forEach(comment => {
          if (comment.parentId && map.has(comment.parentId)) {
            const parent = map.get(comment.parentId);
            if (parent) {
              if (!parent.replies) {
                parent.replies = [];
              }
              parent.replies.push(comment);
            }
          } else {
            tree.push(comment);
          }
        });

        setComments(tree);
       }
        
    }
  });

  return () => unsub();
}, [newsId, routineKey, dateKey, prefs.country, prefs.state, prefs.city, prefs.language]);


  // Build reply tree
const buildTree = (parentId: string | null): NewsComment[] =>
  comments
    .filter(c => c.parentId === parentId && (isRoutine || c.newsId === newsId))
    .map(c => ({ ...c, replies: buildTree(c.id) }));

//   const tree = buildTree(null);
// // For routine posts, comments already contain replies
// const tree = isRoutine ? comments : buildTree(null);

// Determine which tree to use
    let tree: NewsComment[];
    if (isRoutine && routineKey && routineKey !== prefs.city  && routineKey !== 'events') {
      // Routine posts already have replies populated
      tree = comments;
    } else {
      // Regular news or city news: build tree from parentId
      tree = buildTree(null);
    }

  
  // Submit root or reply
  const submit = async (parentId: string | null) => {
    if (!title.trim() || !description.trim()) return;

    if (isRoutine && !isLoggedIn) {
      onLoginClick?.();
      return;
    }

    const colRef = getCommentsCollection(parentId);
    //console.log('colRef for submitting comment:', colRef?.path);
    if (!colRef) {
      alert('Missing location or news information');
      return;
    }
    //console.log('addDocSubmitting comment to', isRoutine ? `routine ${routineKey}` : `news ${newsId}`, 'parentId:', parentId);
    await addDoc(colRef, {
      parentId,
      newsId: newsId ?? null,
      routineKey: routineKey ?? null,
      country: prefs.country ?? null,
      state: prefs.state ?? null,
      city: prefs.city ?? null,
      language: prefs.language ?? null,
      user: prefs.userName || 'Anonymous',
      title: title.trim(),
      description: description.trim(),
      imageUrl: imageUrl.trim(),
      timestamp: Date.now(),
    });

    setTitle('');
    setDescription('');
    setImageUrl('');
    setActiveReplyId(null);
  };

  const renderNodes = (nodes: NewsComment[]) =>
    nodes.map(c => (
      <div key={c.id}>
      <div
  style={{
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    gap: '6px',
    flexWrap: 'nowrap', // prevent wrapping
  }}
>
  <span style={{ fontWeight: 600 }}>{c.user}</span>
  <span style={{ color: '#888', fontSize: '11px' }}>· {new Date(c.timestamp).toLocaleString()}</span>
  <span style={{ fontWeight: 600 }}>{c.title}</span>
  <span>{c.description}</span>

  {c.imageUrl && (
    <img
      src={c.imageUrl}
      alt={c.title}
      style={{ maxWidth: '120px', borderRadius: '4px' }}
    />
  )}

  {isLoggedIn && (<button
    style={{
      fontSize: '11px',
      padding: '0 6px',
      height: '18px',
      flexShrink: 0,
      cursor: 'pointer',
    }}
    onClick={() => setActiveReplyId(c.id)}
  >
    Reply
  </button>)}
</div>
        {activeReplyId === c.id && isLoggedIn && (
          <div className={styles.inputRow}>
            <input
              className={styles.input}
              placeholder="Title"
              disabled={isRoutine && !isLoggedIn}
              value={title}
              onChange={e => setTitle(e.target.value)}
            />
            <input
              className={styles.input}
              placeholder="Description"
              disabled={isRoutine && !isLoggedIn}
              value={description}
              onChange={e => setDescription(e.target.value)}
            />
            <input
              className={styles.input}
              placeholder="Image URL"
              disabled={isRoutine && !isLoggedIn}
              value={imageUrl}
              onChange={e => setImageUrl(e.target.value)}
            />
            <Button size="small" onClick={() => submit(c.id)}>Commit</Button>
          </div>
        )}

        {c.replies?.length ? <div className={styles.children}>{renderNodes(c.replies)}</div> : null}
      </div>
    ));

  return (
    <div className={styles.root}>
       {isLoggedIn && routineKey && (
          (routineKey !== prefs.city && cityRoutineCategories.some(c => c.key === routineKey))
          || 
          (!cityRoutineCategories.some(c => c.key === routineKey) && newsId
          )
        ) && (
      <div className={styles.inputRow}>
        <input
          className={styles.input}
          placeholder="Title"
          disabled={isRoutine && !isLoggedIn}
          value={title}
          onChange={e => setTitle(e.target.value)}
        />
        <input
          className={styles.input}
          placeholder="Description"
          disabled={isRoutine && !isLoggedIn}
          value={description}
          onChange={e => setDescription(e.target.value)}
        />
        <input
          className={styles.input}
          placeholder="Image url"
          disabled={isRoutine && !isLoggedIn}
          value={imageUrl}
          onChange={e => setImageUrl(e.target.value)}
        />

        {!(isRoutine && !isLoggedIn) && (
          <Button size="small" onClick={() => submit(null)}>Post</Button>
        )}
      </div>
   )}
      {isRoutine && !isLoggedIn && (
        // <Text className={styles.loginHint}>Login required for comment</Text>
         <Button size="small" onClick={onLoginClick}>Login / Sign up</Button>
      )}

      {renderNodes(tree)}
    </div>
  );
};
